import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {ConceptRelation, ConceptRelationAttribute, ConceptSelection} from '@twpub/core/models';
import {ConceptService, SharedStateService} from '@twpub/core/services';
import {FieldService} from '@twpub/core/services/field.service';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {CardContentComponent} from '../card-layout/card-content/card-content.component';

@Component({
  selector: 'pub-render-relation',
  templateUrl: './render-relation.component.html',
  styleUrls: ['./render-relation.component.scss'],
  standalone: true,
  imports: [MatCard, MatCardHeader, MatCardTitle, MatCardContent, CardContentComponent, MatIcon, MatTooltip]
})
export class RenderRelationComponent implements OnInit, OnChanges {
  @Input() relation!: ConceptRelation;
  @Input() isReversed: boolean = false;
  @Input() textComponent: boolean = false;

  @Output() conceptSelected = new EventEmitter<ConceptSelection>();

  targetTermName: string = '';
  targetConceptId: number = 0;
  targetTermId: number = 0;
  imageFields: string[] = [];

  constructor(private sharedStateService: SharedStateService, private conceptService: ConceptService, private fieldService: FieldService) {
  }

  ngOnInit(): void {
    this.imageFields = this.fieldService.getImageFields();
    this.loadRelatedConcept();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['relation'] || changes['isReversed']) {
      this.loadRelatedConcept();
    }
  }

  private getRelatedConceptId = () => this.isReversed ? this.relation.sourceConceptId : this.relation.targetConceptId;

  private loadRelatedConcept(): void {
    const relatedConceptId = this.getRelatedConceptId();
    if (!relatedConceptId) {
      this.targetConceptId = 0;
      this.targetTermId = 0;
      this.targetTermName = '';
      return;
    }

    const srcLangs = this.sharedStateService.getSourceLangs();
    this.conceptService.getTermsInLanguages(relatedConceptId, srcLangs).subscribe(terms => {
      this.targetConceptId = relatedConceptId;
      this.targetTermName = terms[0]?.name || '';
      this.targetTermId = terms[0]?.id || 0;
    });
  }

  getTooltipForRelAttr(attr: ConceptRelationAttribute, reverse?: boolean): string {
    const {
      TEMPORAL_CONCURRENT,
      SPATIAL_LEFT, SPATIAL_RIGHT, SPATIAL_UP, SPATIAL_DOWN,
      PARTITIVE_SUPERORDINATE, PARTITIVE_SUBORDINATE,
      TEMPORAL_BEFORE, TEMPORAL_AFTER,
      GENERIC_SUPERORDINATE, GENERIC_SUBORDINATE,
      ASSOCIATIVE_DEFAULT
    } = ConceptRelationAttribute;
    const effective = reverse ? this.reverseAttr(attr) : attr;
    switch (effective) {
      case GENERIC_SUPERORDINATE:   return $localize`:@@relation.direction.genericSuper:Broader concept`;
      case GENERIC_SUBORDINATE:     return $localize`:@@relation.direction.genericSub:Narrower concept`;
      case PARTITIVE_SUPERORDINATE: return $localize`:@@relation.direction.partitiveSuper:Whole`;
      case PARTITIVE_SUBORDINATE:   return $localize`:@@relation.direction.partitiveSub:Part of`;
      case TEMPORAL_BEFORE:         return $localize`:@@relation.direction.temporalBefore:Before`;
      case TEMPORAL_AFTER:          return $localize`:@@relation.direction.temporalAfter:After`;
      case TEMPORAL_CONCURRENT:     return $localize`:@@relation.direction.temporalConcurrent:Concurrent with`;
      case SPATIAL_UP:              return $localize`:@@relation.direction.spatialUp:Above`;
      case SPATIAL_DOWN:            return $localize`:@@relation.direction.spatialDown:Below`;
      case SPATIAL_LEFT:            return $localize`:@@relation.direction.spatialLeft:To the left of`;
      case SPATIAL_RIGHT:           return $localize`:@@relation.direction.spatialRight:To the right of`;
      case ASSOCIATIVE_DEFAULT:     return $localize`:@@relation.direction.associative:Related to`;
    }
    return '';
  }

  private reverseAttr(attr: ConceptRelationAttribute): ConceptRelationAttribute {
    const {
      GENERIC_SUPERORDINATE, GENERIC_SUBORDINATE,
      PARTITIVE_SUPERORDINATE, PARTITIVE_SUBORDINATE,
      TEMPORAL_BEFORE, TEMPORAL_AFTER,
      SPATIAL_UP, SPATIAL_DOWN, SPATIAL_LEFT, SPATIAL_RIGHT,
    } = ConceptRelationAttribute;
    switch (attr) {
      case GENERIC_SUPERORDINATE:   return GENERIC_SUBORDINATE;
      case GENERIC_SUBORDINATE:     return GENERIC_SUPERORDINATE;
      case PARTITIVE_SUPERORDINATE: return PARTITIVE_SUBORDINATE;
      case PARTITIVE_SUBORDINATE:   return PARTITIVE_SUPERORDINATE;
      case TEMPORAL_BEFORE:         return TEMPORAL_AFTER;
      case TEMPORAL_AFTER:          return TEMPORAL_BEFORE;
      case SPATIAL_UP:              return SPATIAL_DOWN;
      case SPATIAL_DOWN:            return SPATIAL_UP;
      case SPATIAL_LEFT:            return SPATIAL_RIGHT;
      case SPATIAL_RIGHT:           return SPATIAL_LEFT;
      default:                      return attr;
    }
  }

  getIconForRelAttr(attr: ConceptRelationAttribute, reverse?: boolean): string {
    const {
      TEMPORAL_CONCURRENT,
      SPATIAL_LEFT,
      SPATIAL_RIGHT,
      SPATIAL_UP,
      SPATIAL_DOWN,
      PARTITIVE_SUPERORDINATE,
      TEMPORAL_BEFORE,
      TEMPORAL_AFTER,
      GENERIC_SUBORDINATE,
      ASSOCIATIVE_DEFAULT,
      PARTITIVE_SUBORDINATE,
      GENERIC_SUPERORDINATE
    } = ConceptRelationAttribute;
    switch (attr) {
      case GENERIC_SUPERORDINATE:
      case PARTITIVE_SUPERORDINATE:
      case SPATIAL_UP:
        return reverse ? 'arrow_downward' : 'arrow_upward';
      case GENERIC_SUBORDINATE:
      case PARTITIVE_SUBORDINATE:
      case SPATIAL_DOWN:
        return reverse ? 'arrow_upward' : 'arrow_downward';
      case TEMPORAL_BEFORE:
      case SPATIAL_LEFT:
        return 'arrow_back';
      case TEMPORAL_AFTER:
      case SPATIAL_RIGHT:
        return 'arrow_forward';
      case TEMPORAL_CONCURRENT:
        return 'sync';
      case ASSOCIATIVE_DEFAULT:
        return 'swap_horiz';
    }
  }

  clickRelation(): void {
    const conceptId = this.targetConceptId;
    const termId = this.targetTermId;
    this.conceptSelected.emit({conceptId, termId});
  }
}
